import React, { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Container, Row, Col, Button, Modal, Form, Alert, Tabs, Tab, Card, Image, Badge } from 'react-bootstrap';
import {
    fetchProdutosFornecedor,
    criarSolicitacaoCompra,
    fetchCategorias,
    atualizarItemCompra
} from '../../services/api';
import Loading from '../../components/Loading/Loading';
import SmartSearch from '../../components/SmartSearch';
import ProdutoInformacao from '../../components/ProdutoInformacao/ProdutoInformacao';
import productTableStyles from '../../components/ProductTable/ProductTable.module.css';

const URGENCIA_CONFIG = {
    baixa: { label: 'Baixa', color: 'secondary' },
    normal: { label: 'Normal', color: 'info' },
    alta: { label: 'Alta', color: 'warning' },
    urgente: { label: 'Urgente', color: 'danger' }
};

// Fornecedores por origem
const FORNECEDORES_ECOMMERCE = [
    'BRUNA LIMEIRA',
    'KARINA LIMEIRA',
    'PRISCILA LIMEIRA',
    'PATRÍCIA LIMEIRA',
    'LUCAS LIMEIRA',
    'ADAILSON ANDRADE',
    'FENIX'
];

const FORNECEDORES_LOJA_FISICA = [
    'BRUNA J.A',
    'KARINA J.A',
    'LUCAS J.A',
    'PRISCILA J.A',
    'PATRICIA J.A'
];

const LimeiraProdutos = () => {
    // 'ALL' carrega todos os fornecedores J.A, string vazia ou nome especifico filtra
    const [selectedFornecedor, setSelectedFornecedor] = useState('ALL');
    const [selectedCategoria, setSelectedCategoria] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('ecommerce');
    const [showModal, setShowModal] = useState(false);
    const [alert, setAlert] = useState(null);

    // States para Lote
    const [showLoteModal, setShowLoteModal] = useState(false);
    const [loteTexto, setLoteTexto] = useState('');
    const [loteProcessing, setLoteProcessing] = useState(false);
    const [loteResult, setLoteResult] = useState(null);

    // States para Duplicados
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateInfo, setDuplicateInfo] = useState(null);
    const [newQuantity, setNewQuantity] = useState(1);

    // States para Tooltip
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [activeProduct, setActiveProduct] = useState(null);

    const [formData, setFormData] = useState({
        produto_codigo: '',
        produto_descricao: '',
        fornecedor: '',
        quantidade_solicitada: 1,
        preco_custo: '',
        urgencia: 'normal',
        observacoes: '',
        origem: 'E-commerce'
    });

    // Buscar produtos - filtra por fornecedor e aba (origem)
    // DEBUG: Log para verificar parâmetros sendo enviados
    console.log('[LimeiraProdutos] State atual - fornecedor:', selectedFornecedor, 'loja:', activeTab);

    const { data: produtosData, isLoading: isLoadingProdutos } = useQuery({
        queryKey: ['produtos-fornecedor', selectedFornecedor, activeTab],
        queryFn: async ({ queryKey }) => {
            const [, fornecedor, loja] = queryKey;
            console.log('[API Call] fetchProdutosFornecedor chamada com queryKey:', fornecedor, loja);
            return fetchProdutosFornecedor(fornecedor, loja);
        },
        staleTime: 30000,  // Reduzido para 30s
        refetchOnMount: 'always'
    });


    const criarMutation = useMutation({
        mutationFn: criarSolicitacaoCompra,
        onSuccess: () => {
            setShowModal(false);
            setAlert({ type: 'success', message: 'Solicitação criada com sucesso!' });
            setTimeout(() => setAlert(null), 5000);
        },
        onError: (error) => {
            // Verificar se é erro de duplicado
            if (error.isDuplicate && error.existingItem) {
                setShowModal(false);
                setDuplicateInfo({
                    ...error.existingItem,
                    message: error.message,
                    suggestion: error.suggestion,
                    newProduct: formData
                });
                setNewQuantity(error.existingItem.quantidade_atual);
                setShowDuplicateModal(true);
            } else {
                setAlert({ type: 'danger', message: error.message || 'Erro ao criar solicitação' });
            }
        }
    });

    // Mutation para atualizar quantidade de item existente
    const atualizarMutation = useMutation({
        mutationFn: ({ id, dados }) => atualizarItemCompra(id, dados),
        onSuccess: () => {
            setShowDuplicateModal(false);
            setDuplicateInfo(null);
            setAlert({ type: 'success', message: 'Quantidade atualizada com sucesso!' });
            setTimeout(() => setAlert(null), 5000);
        },
        onError: (error) => {
            setAlert({ type: 'danger', message: error.message || 'Erro ao atualizar quantidade' });
        }
    });

    const handleUpdateExisting = () => {
        if (duplicateInfo) {
            atualizarMutation.mutate({
                id: duplicateInfo.id,
                dados: { quantidade_solicitada: newQuantity }
            });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        criarMutation.mutate(formData);
    };

    const handleSolicitar = (prod, tipo) => {
        let origemNome = 'E-commerce';
        if (tipo === 'centro') origemNome = 'Loja Centro';
        if (tipo === 'caxias') origemNome = 'Loja Caxias';

        setFormData({
            produto_codigo: prod.codigo,
            produto_descricao: prod.descricao,
            fornecedor: prod.fornecedor || 'N/A',
            preco_custo: prod.custo || '',
            quantidade_solicitada: 1,
            urgencia: 'normal',
            observacoes: '',
            origem: origemNome,
            produto_imagem: prod.imagem // Garante que a imagem vá para a solicitação
        });
        setShowModal(true);
    };

    const handleProcessarLote = async () => {
        if (!loteTexto.trim()) return;
        setLoteProcessing(true);
        setLoteResult(null);

        const lines = loteTexto.split('\n');
        const added = [];
        const notFound = [];
        const errors = [];
        const jaSolicitados = []; // Produtos que já foram solicitados
        const successPromises = [];

        // Mapa de produtos para busca rápida (múltiplas chaves por produto)
        const productMap = new Map();
        if (produtosData?.data) {
            produtosData.data.forEach(p => {
                // Chave principal: código normalizado
                const codigoNorm = p.codigo.trim().toUpperCase();
                productMap.set(codigoNorm, p);

                // Também mapear sem zeros à esquerda
                const codigoSemZeros = codigoNorm.replace(/^0+/, '');
                if (codigoSemZeros && codigoSemZeros !== codigoNorm) {
                    productMap.set(codigoSemZeros, p);
                }

                // Também mapear com código de barras se existir
                if (p.codigoBarras) {
                    const barrasNorm = p.codigoBarras.trim().toUpperCase();
                    productMap.set(barrasNorm, p);
                    // E sem zeros
                    const barrasSemZeros = barrasNorm.replace(/^0+/, '');
                    if (barrasSemZeros && barrasSemZeros !== barrasNorm) {
                        productMap.set(barrasSemZeros, p);
                    }
                }
            });
        }

        // Definir origem baseada na aba ativa
        let origemNome = 'E-commerce';
        if (activeTab === 'centro') origemNome = 'Loja Centro';
        if (activeTab === 'caxias') origemNome = 'Loja Caxias';

        for (let line of lines) {
            line = line.trim();
            if (!line) continue;

            // Tenta separar por Tab, Espaço ou ponto e vírgula
            // Formato esperado: CODIGO  QUANTIDADE
            const match = line.match(/^(.+?)[\s\t;,]+(\d+)$/);

            if (match) {
                let codigo = match[1].trim().toUpperCase();
                const qtd = parseInt(match[2], 10);

                // Tentar encontrar o produto de várias formas
                let prod = productMap.get(codigo);

                // Se não encontrou, tentar sem zeros à esquerda
                if (!prod && /^\d+$/.test(codigo)) {
                    prod = productMap.get(codigo.replace(/^0+/, ''));
                }

                // Se não encontrou, tentar com zeros até 13 dígitos
                if (!prod && /^\d+$/.test(codigo) && codigo.length < 13) {
                    prod = productMap.get(codigo.padStart(13, '0'));
                }

                if (prod) {
                    const payload = {
                        produto_codigo: prod.codigo,
                        produto_descricao: prod.descricao,
                        fornecedor: prod.fornecedor || 'N/A',
                        preco_custo: prod.custo || '',
                        quantidade_solicitada: qtd,
                        urgencia: 'normal',
                        observacoes: 'Via Lote',
                        origem: origemNome,
                        produto_imagem: prod.imagem
                    };

                    successPromises.push(
                        criarSolicitacaoCompra(payload)
                            .then(() => added.push(`${codigo} (Qt: ${qtd})`))
                            .catch(err => {
                                const msg = err.message || '';
                                if (msg.includes('já foi solicitado') || msg.includes('aguardando aprovação')) {
                                    jaSolicitados.push(codigo);
                                } else {
                                    errors.push(`${codigo}: ${msg}`);
                                }
                            })
                    );
                } else {
                    notFound.push(codigo);
                }
            } else {
                errors.push(`Linha inválida: "${line}"`);
            }
        }

        await Promise.all(successPromises);

        setLoteResult({ added, notFound, errors, jaSolicitados });
        setLoteProcessing(false);

        // Fechar se todos foram adicionados (ou já existiam) e não há erros
        if (added.length > 0 && notFound.length === 0 && errors.length === 0 && jaSolicitados.length === 0) {
            setTimeout(() => {
                setShowLoteModal(false);
                setLoteTexto('');
                setLoteResult(null);
                setAlert({ type: 'success', message: `${added.length} solicitações criadas via lote!` });
            }, 1500);
        }
    };

    const formatCurrency = (value) => {
        if (!value && value !== 0) return 'R$ -';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    // Tooltip Handlers
    const handleMouseEnter = useCallback((event, produto) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const tooltipWidth = 180;
        const tooltipHeight = 200; // Altura estimada do tooltip
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const gap = 8; // Espaço entre o card e o tooltip

        // Posicionar ao lado direito do card por padrão
        let left = rect.right + gap;

        // Se não couber à direita, posicionar à esquerda
        if (left + tooltipWidth > windowWidth - gap) {
            left = rect.left - tooltipWidth - gap;
        }

        // Se não couber à esquerda também, centralizar
        if (left < gap) {
            left = gap;
        }

        // Posicionar verticalmente alinhado com o topo do card
        let top = rect.top;

        // Se ultrapassar a parte inferior da tela, ajustar
        if (top + tooltipHeight > windowHeight - gap) {
            top = windowHeight - tooltipHeight - gap;
        }

        // Garantir que não fique acima da tela
        if (top < gap) {
            top = gap;
        }

        setTooltipPosition({
            top,
            left,
            maxHeight: windowHeight - gap * 2
        });
        setActiveProduct(produto);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setActiveProduct(null);
    }, []);

    const renderProductCard = (prod, tipo) => {
        // Vendas do mês atual para cada canal
        const vendas = tipo === 'ecommerce'
            ? (prod.ecommerce?.vendas_mes || 0)
            : tipo === 'centro'
                ? (prod.loja_fisica?.vendas_mes_centro || 0)
                : (prod.loja_fisica?.vendas_mes_caxias || 0);

        const estoque = tipo === 'ecommerce'
            ? (prod.ecommerce?.estoque || 0)
            : tipo === 'centro'
                ? (prod.loja_fisica?.qtd_centro || 0)
                : tipo === 'caxias'
                    ? (prod.loja_fisica?.qtd_caxias || 0)
                    : (prod.loja_fisica?.estoque || 0);

        // Processar informações/tags do produto
        const getInformacoes = () => {
            if (!prod.informacao) return [];
            if (Array.isArray(prod.informacao)) return prod.informacao;
            if (typeof prod.informacao === 'string') {
                try {
                    const parsed = JSON.parse(prod.informacao);
                    if (Array.isArray(parsed)) return parsed;
                    return [parsed];
                } catch {
                    return [prod.informacao];
                }
            }
            return [];
        };

        const informacoes = getInformacoes();
        const informacoesUnicas = [...new Set(informacoes.filter(Boolean))];

        // Verificar se tem tag "Não Comprar"
        const temTagNaoComprar = informacoesUnicas.some(info => {
            const lower = String(info).toLowerCase();
            return lower === 'nao_comprar' || lower === 'não comprar' || lower === 'nao comprar';
        });

        // Funções para exibir badges
        const getBadgeLabel = (info) => {
            const map = {
                'recorrente': 'Recorrente',
                'nao_comprar': 'Não Comprar',
                'promocao': 'Promoção',
                'novo': 'Novo',
                'destaque': 'Destaque',
                'baixa_rotacao': 'Analise'
            };
            const key = Object.keys(map).find(k => k.toLowerCase() === String(info).toLowerCase());
            return key ? map[key] : info;
        };

        const getBadgeColor = (info) => {
            const colors = {
                'recorrente': 'primary',
                'nao_comprar': 'danger',
                'promocao': 'warning',
                'novo': 'success',
                'destaque': 'info',
                'baixa_rotacao': 'secondary'
            };
            const key = Object.keys(colors).find(k => k.toLowerCase() === String(info).toLowerCase());
            return key ? colors[key] : 'secondary';
        };

        return (
            <Card
                className="h-100 shadow-sm border-0"
                style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                onMouseEnter={(e) => handleMouseEnter(e, prod)}
                onMouseLeave={handleMouseLeave}
            >
                <div style={{ position: 'relative', paddingTop: '100%', background: '#f8f9fa' }}>
                    {prod.imagem ? (
                        <Image
                            src={prod.imagem}
                            alt={prod.descricao}
                            loading="lazy"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '2rem',
                                color: '#e2e8f0'
                            }}
                        >
                            📷
                        </div>
                    )}
                    {/* Badges de vendas - canto superior esquerdo */}
                    {vendas > 0 && (
                        <Badge
                            bg="info"
                            style={{ position: 'absolute', top: '5px', left: '5px', fontSize: '0.65rem' }}
                        >
                            {vendas} vd
                        </Badge>
                    )}
                    {estoque <= 0 && (
                        <Badge
                            bg="danger"
                            style={{ position: 'absolute', top: vendas > 0 ? '25px' : '5px', left: '5px', fontSize: '0.65rem' }}
                        >
                            Sem Est.
                        </Badge>
                    )}
                    {/* Tags de informação - canto superior direito */}
                    {informacoesUnicas.length > 0 && (
                        <div style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            alignItems: 'flex-end'
                        }}>
                            {informacoesUnicas.map((info, index) => (
                                <Badge
                                    key={index}
                                    bg={getBadgeColor(info)}
                                    style={{ fontSize: '0.6rem', fontWeight: 'normal' }}
                                >
                                    {getBadgeLabel(info)}
                                </Badge>
                            ))}
                        </div>
                    )}
                    {/* Tag de Solicitação de Compra Pendente */}
                    {prod.temSolicitacao && (
                        <Badge
                            bg="warning"
                            text="dark"
                            style={{
                                position: 'absolute',
                                bottom: '5px',
                                left: '5px',
                                fontSize: '0.65rem'
                            }}
                            title={`Solicitações pendentes: ${prod.solicitacoes.map(s => `${s.quantidade}x (${s.mes})`).join(', ')}`}
                        >
                            📋 {prod.solicitacoes.reduce((sum, s) => sum + s.quantidade, 0)}
                        </Badge>
                    )}
                </div>
                <Card.Body className="p-2">
                    <div
                        style={{
                            fontSize: '0.85rem',
                            color: '#3b82f6',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(prod.codigo);
                            const el = e.currentTarget;
                            const original = el.innerHTML;
                            el.innerHTML = '✅ Copiado!';
                            el.style.color = '#22c55e';
                            setTimeout(() => {
                                el.innerHTML = original;
                                el.style.color = '#3b82f6';
                            }, 1000);
                        }}
                        title="Clique para copiar o código"
                    >
                        📋 {prod.codigo}
                    </div>
                    {prod.codigoBarras && (
                        <div
                            style={{
                                fontSize: '0.75rem',
                                color: '#64748b',
                                cursor: 'pointer',
                                marginTop: '2px'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(prod.codigoBarras);
                                const el = e.currentTarget;
                                const original = el.innerHTML;
                                el.innerHTML = '✅ Copiado!';
                                el.style.color = '#22c55e';
                                setTimeout(() => {
                                    el.innerHTML = original;
                                    el.style.color = '#64748b';
                                }, 1000);
                            }}
                            title="Clique para copiar o código de barras"
                        >
                            🔖 {prod.codigoBarras}
                        </div>
                    )}
                    <div className="fw-bold text-truncate mb-1" title={prod.descricao} style={{ fontSize: '0.8rem', color: '#1e293b', marginTop: '4px' }}>
                        {prod.descricao}
                    </div>
                    <div className="text-primary fw-bold" style={{ fontSize: '0.9rem' }}>{formatCurrency(prod.custo)}</div>
                    <div className="d-flex justify-content-between mt-1 text-muted" style={{ fontSize: '0.7rem' }}>
                        <span>Est: {estoque}</span>
                        <span>{vendas} vd/mês</span>
                    </div>
                    {/* Componente para editar tags do produto */}
                    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                        <ProdutoInformacao
                            produtoCodigo={prod.codigo}
                            valorAtual={prod.informacao}
                            tipo={tipo === 'ecommerce' ? 'ecommerce' : 'loja-fisica'}
                            onUpdate={(novasTags) => {
                                // Atualizar o produto localmente após salvar
                                prod.informacao = novasTags;
                            }}
                        />
                    </div>
                </Card.Body>
                <Card.Footer className="p-2 bg-white border-top-0">
                    <Button
                        variant={temTagNaoComprar ? "secondary" : "success"}
                        size="sm"
                        className="w-100 py-1"
                        style={{ fontSize: '0.8rem' }}
                        onClick={() => handleSolicitar(prod, tipo)}
                        disabled={temTagNaoComprar}
                        title={temTagNaoComprar ? "Este produto está marcado como 'Não Comprar'" : "Solicitar compra deste produto"}
                    >
                        {temTagNaoComprar ? '🚫 Não Comprar' : '🛒 Solicitar'}
                    </Button>
                </Card.Footer>
            </Card>
        );
    };

    // Buscar categorias do sistema
    // Extrair categorias dinamicamente dos produtos carregados
    const categorias = React.useMemo(() => {
        if (!produtosData?.data) return [];
        try {
            const cats = new Set(produtosData.data
                .map(p => p.categoria ? String(p.categoria).trim() : 'Outros')
                .filter(c => c && c.length > 0)
            );
            return Array.from(cats).sort();
        } catch (e) {
            console.error('Erro ao processar categorias', e);
            return [];
        }
    }, [produtosData]);

    // Filtrar dados uma única vez
    const produtosFiltrados = React.useMemo(() => {
        if (!produtosData?.data) return [];
        let dados = [...produtosData.data];

        if (selectedCategoria && selectedCategoria !== 'ALL') {
            dados = dados.filter(p => {
                const cat = p.categoria ? String(p.categoria).trim() : 'Outros';
                return cat === selectedCategoria;
            });
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            dados = dados.filter(prod =>
                (prod.codigo || '').toLowerCase().includes(term) ||
                (prod.descricao || '').toLowerCase().includes(term) ||
                (prod.codigoBarras || '').toLowerCase().includes(term)
            );
        }
        return dados;
    }, [produtosData, searchTerm, selectedCategoria]);

    const renderGrid = (tipo) => {
        if (produtosFiltrados.length === 0) return <div className="text-center p-3 text-muted">Nenhum produto encontrado com este filtro.</div>;

        let dados = [...produtosFiltrados];

        // Ordenar por vendas do mês atual (igual ao catálogo E-commerce)
        if (tipo === 'ecommerce') {
            dados.sort((a, b) => (b.ecommerce?.vendas_mes || 0) - (a.ecommerce?.vendas_mes || 0));
        } else if (tipo === 'centro') {
            dados.sort((a, b) => (b.loja_fisica?.vendas_mes_centro || 0) - (a.loja_fisica?.vendas_mes_centro || 0));
        } else if (tipo === 'caxias') {
            dados.sort((a, b) => (b.loja_fisica?.vendas_mes_caxias || 0) - (a.loja_fisica?.vendas_mes_caxias || 0));
        }

        return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '1rem' }}>
                {dados.map(prod => (
                    <div key={prod.codigo}>
                        {renderProductCard(prod, tipo)}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <Container fluid className="p-4">
            <h1 className="h3 mb-4 text-gray-800">📦 Catálogo de Produtos - Limeira</h1>

            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="mb-4">
                    {alert.message}
                </Alert>
            )}

            {/* Barra de Filtros Dark Mode */}
            <div className="p-3 mb-4 rounded shadow-sm d-flex align-items-end gap-3 flex-wrap" style={{ backgroundColor: '#0f172a', border: '1px solid #1e293b' }}>
                <div style={{ minWidth: '200px', flex: 1 }}>
                    <label className="d-block text-uppercase mb-1" style={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.5px' }}>
                        Fornecedor
                    </label>
                    <Form.Select
                        value={selectedFornecedor}
                        onChange={(e) => setSelectedFornecedor(e.target.value)}
                        style={{
                            backgroundColor: '#1e293b',
                            color: '#f8fafc',
                            border: '1px solid #334155',
                            fontSize: '0.9rem',
                            height: '42px'
                        }}
                    >
                        <option value="ALL">Todos</option>
                        {(activeTab === 'ecommerce' ? FORNECEDORES_ECOMMERCE : FORNECEDORES_LOJA_FISICA).map(f => (
                            <option key={f} value={f}>{f}</option>
                        ))}
                    </Form.Select>
                </div>

                <div style={{ minWidth: '200px', flex: 1 }}>
                    <label className="d-block text-uppercase mb-1" style={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.5px' }}>
                        Categoria
                    </label>
                    <Form.Select
                        value={selectedCategoria}
                        onChange={(e) => setSelectedCategoria(e.target.value)}
                        style={{
                            backgroundColor: '#1e293b',
                            color: '#f8fafc',
                            border: '1px solid #334155',
                            fontSize: '0.9rem',
                            height: '42px'
                        }}
                    >
                        <option value="ALL">Todas</option>
                        {categorias.map(c => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </Form.Select>
                </div>

                <div style={{ minWidth: '350px', flex: 2 }}>
                    <label className="d-block text-uppercase mb-1" style={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.5px' }}>
                        Busca Inteligente
                    </label>
                    <div className="d-flex gap-2">
                        <SmartSearch
                            placeholder="Buscar código, descrição ou fornecedor..."
                            searchEndpoint={`/api/search/suggestions?tipo=${activeTab === 'ecommerce' ? 'ecommerce' : 'loja-fisica'}`}
                            storageKey="limeira-search-history"
                            onSearch={(term) => setSearchTerm(term)}
                            onSelect={(item) => {
                                // Quando selecionar um item diretamente, podemos abrir o modal de solicitação
                                if (item && item.codigo) {
                                    setSearchTerm(item.descricao || item.codigo);
                                }
                            }}
                        />
                        <button
                            onClick={() => setShowLoteModal(true)}
                            className="btn d-flex align-items-center gap-2 shadow-sm"
                            style={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                height: '42px',
                                fontWeight: '500',
                                whiteSpace: 'nowrap',
                                padding: '0 20px',
                                borderRadius: '6px'
                            }}
                        >
                            <span>📋</span> Lote
                        </button>
                    </div>
                </div>
            </div>

            <Card className="shadow mb-4">
                <Card.Body>
                    {isLoadingProdutos ? (
                        <Loading />
                    ) : !produtosData?.data?.length ? (
                        <div className="text-center p-5">
                            <h5 className="text-muted">Nenhum produto encontrado.</h5>
                        </div>
                    ) : (
                        <>
                            <Alert variant="info" className="py-2 mb-3">
                                <small>📝 Mostrando <strong>{produtosFiltrados.length}</strong> produtos encontrados (Total carregado: {produtosData.data.length})</small>
                            </Alert>

                            <Tabs activeKey={activeTab} onSelect={(k) => { setActiveTab(k); setSelectedFornecedor('ALL'); }} className="mb-3">
                                <Tab eventKey="ecommerce" title="🛒 E-commerce">
                                    <div className="p-2">{renderGrid('ecommerce')}</div>
                                </Tab>
                                <Tab eventKey="centro" title="🏢 Loja Centro">
                                    <div className="p-2">{renderGrid('centro')}</div>
                                </Tab>
                                <Tab eventKey="caxias" title="🏪 Loja Caxias">
                                    <div className="p-2">{renderGrid('caxias')}</div>
                                </Tab>
                            </Tabs>
                        </>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>➕ Nova Solicitação: {formData.produto_descricao}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Código</Form.Label>
                                    <Form.Control type="text" value={formData.produto_codigo} readOnly disabled />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Fornecedor</Form.Label>
                                    <Form.Control type="text" value={formData.fornecedor} readOnly disabled />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Quantidade *</Form.Label>
                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            style={{ minWidth: '48px', fontWeight: 'bold' }}
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                quantidade_solicitada: Math.max(1, (parseInt(prev.quantidade_solicitada) || 1) - 10)
                                            }))}
                                        >−10</button>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            max="10000"
                                            value={formData.quantidade_solicitada}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === '') {
                                                    setFormData(prev => ({ ...prev, quantidade_solicitada: '' }));
                                                } else {
                                                    const n = parseInt(v);
                                                    setFormData(prev => ({ ...prev, quantidade_solicitada: isNaN(n) ? 1 : Math.min(10000, Math.max(1, n)) }));
                                                }
                                            }}
                                            onBlur={() => setFormData(prev => ({
                                                ...prev,
                                                quantidade_solicitada: Math.max(1, parseInt(prev.quantidade_solicitada) || 1)
                                            }))}
                                            required
                                            style={{ textAlign: 'center', fontWeight: 'bold' }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            style={{ minWidth: '48px', fontWeight: 'bold' }}
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                quantidade_solicitada: Math.min(10000, (parseInt(prev.quantidade_solicitada) || 1) + 10)
                                            }))}
                                        >+10</button>
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Preço Custo (R$)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={formData.preco_custo}
                                        onChange={(e) => setFormData({ ...formData, preco_custo: parseFloat(e.target.value) || '' })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Urgência</Form.Label>
                                    <Form.Select
                                        value={formData.urgencia}
                                        onChange={(e) => setFormData({ ...formData, urgencia: e.target.value })}
                                    >
                                        {Object.entries(URGENCIA_CONFIG).map(([key, config]) => (
                                            <option key={key} value={key}>{config.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Observações</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Informações adicionais..."
                                value={formData.observacoes}
                                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                        <Button variant="primary" type="submit" disabled={criarMutation.isPending}>
                            {criarMutation.isPending ? 'Criando...' : '✅ Confirmar Solicitação'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de Lote */}
            <Modal show={showLoteModal} onHide={() => setShowLoteModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-dark text-white">
                    <Modal.Title>📋 Solicitação em Lote ({activeTab === 'centro' ? 'Loja Centro' : activeTab === 'caxias' ? 'Loja Caxias' : 'E-commerce'})</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info" className="mb-3">
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <small>
                                    <strong>📥 Opção 1:</strong> Baixe a planilha modelo, preencha e cole aqui.<br />
                                    <strong>📝 Opção 2:</strong> Cole diretamente no formato: <strong>CÓDIGO [Tab/Espaço] QUANTIDADE</strong>
                                </small>
                            </div>
                            <Button
                                variant="success"
                                size="sm"
                                onClick={() => {
                                    // Criar CSV com ponto e vírgula (padrão Excel BR)
                                    const csvContent = "codigo;quantidade\nCP123;10\nT238;20\nP703;30\n";
                                    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = 'modelo_solicitacao_lote.csv';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(url);
                                }}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                📥 Baixar Modelo
                            </Button>
                        </div>
                        <hr className="my-2" />
                        <small className="text-muted">
                            Exemplo de conteúdo:<br />
                            <code>CP123 10</code><br />
                            <code>T238 20</code><br />
                            <code>P703 40</code>
                        </small>
                    </Alert>


                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="fw-bold mb-0">Cole os dados aqui:</Form.Label>
                            {(activeTab === 'centro' || activeTab === 'caxias') && (
                                <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => {
                                        // Formatar códigos para 13 dígitos (código de barras)
                                        const linhas = loteTexto.split('\n');
                                        const linhasFormatadas = linhas.map(linha => {
                                            const partes = linha.trim().split(/[\s\t;,]+/);
                                            if (partes.length >= 2) {
                                                let codigo = partes[0].replace(/\D/g, ''); // só números
                                                const quantidade = partes[1];
                                                if (codigo && codigo.length < 13) {
                                                    codigo = codigo.padStart(13, '0');
                                                }
                                                return `${codigo} ${quantidade}`;
                                            }
                                            return linha;
                                        });
                                        setLoteTexto(linhasFormatadas.join('\n'));
                                    }}
                                    title="Adiciona zeros à esquerda para completar 13 dígitos (código de barras)"
                                >
                                    🔢 Formatar 13 dígitos
                                </Button>
                            )}
                        </div>
                        <Form.Control
                            as="textarea"
                            rows={10}
                            placeholder={activeTab === 'ecommerce'
                                ? "Cole os códigos e quantidades aqui...\nExemplo:\nCP123 10\nT238 20"
                                : "Cole os códigos e quantidades aqui...\nExemplo (código de barras):\n21942 10\n58743 20\n\n💡 Clique em 'Formatar 13 dígitos' para adicionar zeros à esquerda"}
                            value={loteTexto}
                            onChange={(e) => setLoteTexto(e.target.value)}
                            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                        />
                    </Form.Group>

                    {loteResult && (
                        <div className="mt-3">
                            {loteResult.added.length > 0 && (
                                <Alert variant="success" className="py-2 mb-2">
                                    <small>✅ {loteResult.added.length} itens adicionados com sucesso.</small>
                                </Alert>
                            )}
                            {loteResult.jaSolicitados && loteResult.jaSolicitados.length > 0 && (
                                <Alert variant="info" className="py-2 mb-2">
                                    <small>
                                        <strong>ℹ️ {loteResult.jaSolicitados.length} produto(s) já solicitado(s):</strong><br />
                                        Estes produtos já estão na fila de aprovação e não foram duplicados.<br />
                                        <span className="text-muted" style={{ fontSize: '0.85em' }}>
                                            Códigos: {loteResult.jaSolicitados.slice(0, 10).join(', ')}
                                            {loteResult.jaSolicitados.length > 10 && ` e mais ${loteResult.jaSolicitados.length - 10}...`}
                                        </span>
                                    </small>
                                </Alert>
                            )}
                            {loteResult.notFound.length > 0 && (
                                <Alert variant="warning" className="py-2 mb-2">
                                    <small>⚠️ Códigos não encontrados na lista atual: {loteResult.notFound.join(', ')}</small>
                                </Alert>
                            )}
                            {loteResult.errors.length > 0 && (
                                <Alert variant="danger" className="py-2 mb-2">
                                    <small>❌ Erros: {loteResult.errors.join('; ')}</small>
                                </Alert>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-danger" size="sm" onClick={() => setLoteTexto('')} disabled={loteProcessing || !loteTexto}>
                        🗑️ Limpar
                    </Button>
                    <Button variant="secondary" onClick={() => setShowLoteModal(false)} disabled={loteProcessing}>Fechar</Button>
                    <Button variant="primary" onClick={handleProcessarLote} disabled={loteProcessing || !loteTexto.trim()}>
                        {loteProcessing ? 'Processando...' : '🚀 Processar Lote'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Produto Duplicado */}
            <Modal show={showDuplicateModal} onHide={() => setShowDuplicateModal(false)} centered>
                <Modal.Header closeButton className="bg-warning">
                    <Modal.Title>⚠️ Produto Já Solicitado</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {duplicateInfo && (
                        <>
                            <Alert variant="warning" className="mb-3">
                                <strong>{duplicateInfo.message}</strong>
                                <br />
                                <small className="text-muted">{duplicateInfo.suggestion}</small>
                            </Alert>

                            <div className="bg-light p-3 rounded mb-3">
                                <h6 className="mb-2">📦 Solicitação Existente:</h6>
                                <div><strong>Código:</strong> {duplicateInfo.produto_codigo}</div>
                                <div><strong>Descrição:</strong> {duplicateInfo.produto_descricao}</div>
                                <div><strong>Quantidade Atual:</strong> <Badge bg="info">{duplicateInfo.quantidade_atual} un</Badge></div>
                                <div><strong>Status:</strong> <Badge bg={duplicateInfo.status === 'aprovado' ? 'success' : 'warning'}>{duplicateInfo.status}</Badge></div>
                                <div><strong>Origem:</strong> {duplicateInfo.origem}</div>
                                <div><strong>Data:</strong> {new Date(duplicateInfo.data_solicitacao).toLocaleDateString('pt-BR')}</div>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label><strong>Nova Quantidade:</strong></Form.Label>
                                <Form.Control
                                    type="number"
                                    min="1"
                                    value={newQuantity}
                                    onChange={(e) => setNewQuantity(parseInt(e.target.value) || 1)}
                                />
                                <Form.Text className="text-muted">
                                    Altere a quantidade para atualizar a solicitação existente.
                                </Form.Text>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDuplicateModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUpdateExisting}
                        disabled={atualizarMutation.isPending}
                    >
                        {atualizarMutation.isPending ? 'Atualizando...' : '✅ Atualizar Quantidade'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Tooltip Simples - Histórico de Vendas */}
            {activeProduct && (
                <div
                    style={{
                        position: 'fixed',
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`,
                        background: '#1e293b',
                        color: '#f1f5f9',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        zIndex: 9999,
                        minWidth: '180px',
                        fontSize: '12px'
                    }}
                >
                    <div style={{ fontWeight: 600, marginBottom: '8px', color: '#94a3b8', fontSize: '11px' }}>
                        Histórico de Vendas
                    </div>
                    {(() => {
                        // Escolhe o histórico correto pela aba ativa:
                        // Centro/Caxias = vendas SÓ daquela loja (sem mistura entre lojas).
                        // E-commerce/fallback = vendasMensais (já em tempo real pra ecom).
                        const historico =
                            activeTab === 'centro' ? activeProduct.vendasMensaisCentro :
                            activeTab === 'caxias' ? activeProduct.vendasMensaisCaxias :
                            activeProduct.vendasMensais;
                        if (!historico || historico.length === 0) {
                            return (
                                <div style={{ color: '#94a3b8', fontSize: '11px', textAlign: 'center', padding: '8px 0' }}>
                                    Sem histórico
                                </div>
                            );
                        }
                        return historico.slice(0, 6).map((venda, index) => {
                            // Formatar o nome do mês (pode vir como "Jan", "2026-01", número, etc.)
                            const mesesAbrev = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
                            let mesFormatado = venda.mes;

                            // Se for número (1-12), converter para nome
                            if (typeof venda.mes === 'number' || /^\d+$/.test(venda.mes)) {
                                const mesNum = parseInt(venda.mes) - 1;
                                if (mesNum >= 0 && mesNum < 12) {
                                    mesFormatado = mesesAbrev[mesNum];
                                }
                            }
                            // Se for data no formato YYYY-MM ou similar
                            else if (/^\d{4}-\d{2}/.test(venda.mes)) {
                                const mesNum = parseInt(venda.mes.split('-')[1]) - 1;
                                if (mesNum >= 0 && mesNum < 12) {
                                    mesFormatado = mesesAbrev[mesNum];
                                }
                            }
                            // Se já for nome completo, abreviar
                            else if (typeof venda.mes === 'string' && venda.mes.length > 3) {
                                mesFormatado = venda.mes.substring(0, 3).toLowerCase();
                            }
                            // Se for string curta, apenas lowercase
                            else if (typeof venda.mes === 'string') {
                                mesFormatado = venda.mes.toLowerCase();
                            }

                            return (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '4px 0',
                                        borderBottom: index < 5 ? '1px solid #334155' : 'none'
                                    }}
                                >
                                    <span style={{ color: '#94a3b8' }}>{mesFormatado}</span>
                                    <span style={{ fontWeight: 600 }}>{venda.quantidade}</span>
                                </div>
                            );
                        });
                    })()}
                </div>
            )}
        </Container>
    );
};

export default LimeiraProdutos;
